import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { isDescriptionEmpty } from '../../pages/EditOpportunity/PublicationForm/PublicationInfoForm';
import { AIAlert } from '../components/AIAlert';
import { ACTION_GENERATE_INTERVIEW_SCRIPT } from '../constants/actions';
import { aiPurple } from '../constants/colors';
import {
  SinapseBtnMainProps,
  TextOverwriteWithAIGenerated,
} from '../generate_text/TextOverwriteWithAIGenerated';
import useAsyncSinapseResponse, {
  AsyncSinapseResponseHookProps,
} from '../hooks/useAsyncSinapseResponse';
import { SparkIcon } from '../icons/spark';

const getQueryKeyPath = (sourceId: number, interviewKey: string) => [
  'sinapse',
  'interviewScript',
  sourceId,
  interviewKey,
];

export const InterviewScriptGenerator: React.FC<
  AsyncSinapseResponseHookProps & {
    triggerEnabled: boolean;
    interviewKey: string;
  }
> = ({
  enabled,
  setIsDisabledInput,
  setResult,
  sourceId: jobPostingId,
  interviewKey,
  textValue = '',
  input = '',
  triggerEnabled = false,
}) => {
  const { discartResponseResult, processResult, responseStatus } = useAsyncSinapseResponse({
    setIsDisabledInput,
    setResult,
    sourceId: jobPostingId,
    textValue,
    input,
    action: ACTION_GENERATE_INTERVIEW_SCRIPT,
    enabled,
    configs: {
      overwriteOnSuccess: true,
      queryKeyPath:
        jobPostingId && interviewKey !== undefined
          ? getQueryKeyPath(jobPostingId, interviewKey)
          : undefined,
    },
  });

  if (!enabled) {
    return null;
  }

  return (
    <>
      <AIAlert
        key="ai-alert-interview-script"
        showIcon
        icon={<SparkIcon style={{ fontSize: '1.5em', color: aiPurple }} />}
        action={
          <GenerateInterviewScriptButton
            discartResponseText={
              // confirm fn
              discartResponseResult
            }
            processText={processResult} // initiator
            responseTextStatus={responseStatus} // state
            textValue={textValue}
            isTextEmpty={isDescriptionEmpty}
            btnProps={{
              disabled: !triggerEnabled,
            }}
            interviewKey={interviewKey}
          />
        }
        message={
          <>
            <b>
              <Trans>Roteiro de entrevista</Trans>:{' '}
            </b>
            <Trans>
              Utilize a Synapses, a inteligência artificial da Mindsight, para criar um roteiro de
              perguntas de entrevista totalmente alinhado à vaga.
            </Trans>
          </>
        }
      />
    </>
  );
};
export const GenerateInterviewScriptButton: React.FC<
  SinapseBtnMainProps & { interviewKey: string }
> = (props) => {
  const { t } = useTranslation();

  const { disabled } = props.btnProps || {};
  const modalProps = {
    title: t('Sobrescrever o roteiro atual?'),
    content: t(
      'Ao sobrescrever o roteiro atual, você perderá o roteiro atual e receberá um novo roteiro gerado pela Synapses.'
    ),
    okText: t('Sim'),
    cancelText: t('Não'),
  };
  return (
    <TextOverwriteWithAIGenerated
      confirmOverwrite
      {...props}
      confirmProps={modalProps}
      btnContent={
        <Tooltip
          key={props.interviewKey}
          open={disabled}
          autoAdjustOverflow
          getPopupContainer={(triggerNode: HTMLElement) => triggerNode.parentElement as HTMLElement}
          title={
            <>
              <ExclamationCircleOutlined style={{ color: '#faad14', marginLeft: '5px' }} />{' '}
              <Trans>Preencha a descrição da vaga para gerar o roteiro.</Trans>
            </>
          }
          placement="top"
          overlayInnerStyle={{
            maxWidth: 250,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            textAlign: 'center',
          }}
        >
          {' '}
          <Trans>Gerar roteiro</Trans>
        </Tooltip>
      }
    />
  );
};
